/**
 * `cashMovements.*` — the generic money-event writer + readers
 * (ADR-0012 §2). One row per money fact that isn't an invoice / bill /
 * vendor-payment leg.
 *
 * V1 kinds are functional in `postCashMovement`; V1.x kinds
 * (`refund_vendor`, `interest_received`) live in the wire enum so the
 * forward-compat contract is honoured but the domain refuses them.
 */
import { z } from "zod";
import { denominationCount, isoDate, moneyPaise, uuid } from "../_common.js";

export const cashMovementKind = z.enum([
  // V1 (14)
  "transfer",
  "refund_customer",
  "statutory_payment",
  "owner_contribution",
  "owner_drawing",
  "loan_disbursement",
  "loan_repayment",
  "interest_paid",
  "stock_writeoff",
  "petty_cash_expense",
  "cash_count_adjustment",
  "deposit_paid",
  "deposit_received",
  "bank_charge",
  // Payroll Increment 2 (ADR-0026) — per-employee net salary disbursement.
  // Posted by `payroll/runs` `payRun`; NOT offered in the generic
  // `/cash-movements/new` kind picker (that has its own hardcoded list).
  "salary",
  // V1.x (2)
  "refund_vendor",
  "interest_received",
]);

const expenseCategory = z.enum([
  "postage",
  "conveyance",
  "travel",
  "refreshments",
  "office_supplies",
  "printing_stationery",
  "repairs_maintenance",
  "sundries",
]);

const bankChargeSubtype = z.enum([
  "fx_markup",
  "card_annual_fee",
  "late_fee",
  "wire_transfer_fee",
  "atm_fee",
  "account_maintenance",
  "other",
]);

const counterpartyType = z.enum([
  "customer",
  "vendor",
  "membership",
  "lender_freetext",
  // Payroll Increment 2 (ADR-0026) — the salaried employee on a salary movement.
  "employee",
]);

const linkedEntityType = z.enum([
  "invoice",
  "credit_note",
  "bill",
  "tds_challan",
  "gst_return",
  "payroll_run",
  "inventory_adjustment",
]);

// ── inputs ──────────────────────────────────────────────────────────

export const postCashMovementInput = z.object({
  kind: cashMovementKind,
  fromAccountId: uuid.optional(),
  toAccountId: uuid.optional(),
  counterpartyType: counterpartyType.optional(),
  counterpartyId: uuid.optional(),
  counterpartyLabel: z.string().max(120).optional(),
  amountPaise: moneyPaise,
  valueDate: isoDate,
  narration: z.string().min(1).max(2000),
  linkedEntityType: linkedEntityType.optional(),
  linkedEntityId: uuid.optional(),
  expenseCategory: expenseCategory.optional(),
  bankChargeSubtype: bankChargeSubtype.optional(),
  /** Resubmit path after founder approval. */
  approvalId: uuid.optional(),
  /**
   * Reconciliation evidence — only meaningful when
   * `kind=cash_count_adjustment`. Written into the audit `metadata`
   * payload so the petty-cash counts-history surface can render the
   * "book / counted / variance" reconciliation + denomination grid
   * in the expanded row. Accepted silently for other kinds (stored
   * but unused) so the SPA composer can pass them uniformly without
   * a kind branch.
   */
  denominations: denominationCount.optional(),
  bookBalancePaise: z
    .string()
    .regex(/^-?\d+$/, "signed paise must be an integer string")
    .optional(),
  countedPaise: z
    .string()
    .regex(/^-?\d+$/, "signed paise must be an integer string")
    .optional(),
});

export const reverseCashMovementInput = z.object({
  movementId: uuid,
  reason: z.string().min(3).max(500),
});

export const getCashMovementInput = z.object({
  movementId: uuid,
});

export const listCashMovementsInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).optional(),
  bankAccountId: uuid.optional(),
  kind: cashMovementKind.optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
});

// ── outputs ─────────────────────────────────────────────────────────

const movementShape = z.object({
  id: uuid,
  kind: cashMovementKind,
  fromAccountId: uuid.nullable(),
  /** Joined `from_account.name` (null when no from-side or account hidden). */
  fromAccountName: z.string().nullable(),
  toAccountId: uuid.nullable(),
  /** Joined `to_account.name` (null when no to-side or account hidden). */
  toAccountName: z.string().nullable(),
  counterpartyType: counterpartyType.nullable(),
  counterpartyId: uuid.nullable(),
  counterpartyLabel: z.string().nullable(),
  amountPaise: moneyPaise,
  valueDate: isoDate,
  narration: z.string(),
  linkedEntityType: linkedEntityType.nullable(),
  linkedEntityId: uuid.nullable(),
  expenseCategory: expenseCategory.nullable(),
  bankChargeSubtype: bankChargeSubtype.nullable(),
  reversesMovementId: uuid.nullable(),
  /** Set when another row has already reversed THIS movement. */
  reversedByMovementId: uuid.nullable(),
  requiresFounderApproval: z.boolean(),
  approvalId: uuid.nullable(),
  createdByActorId: z.string(),
  createdAt: z.string(),
});

export const postCashMovementOutput = z.object({
  movement: movementShape.nullable(),
  awaitingApproval: z
    .object({
      approvalId: uuid,
      summary: z.string(),
    })
    .nullable(),
});

export const reverseCashMovementOutput = z.object({
  reversal: movementShape,
});

export const getCashMovementOutput = z.object({
  movement: movementShape,
});

const listRow = z.object({
  id: uuid,
  kind: cashMovementKind,
  fromAccountId: uuid.nullable(),
  fromAccountName: z.string().nullable(),
  toAccountId: uuid.nullable(),
  toAccountName: z.string().nullable(),
  counterpartyLabel: z.string().nullable(),
  amountPaise: moneyPaise,
  valueDate: isoDate,
  narration: z.string(),
  linkedEntityType: linkedEntityType.nullable(),
  linkedEntityId: uuid.nullable(),
  /**
   * `cash_movements.expense_category` — surfaced on the list row so the
   * petty-cash activity feed can render the correct category icon without
   * a per-row roundtrip. Only populated when `kind=petty_cash_expense`.
   */
  expenseCategory: expenseCategory.nullable(),
  reversesMovementId: uuid.nullable(),
  reversedByMovementId: uuid.nullable(),
  createdAt: z.string(),
});

export const listCashMovementsOutput = z.object({
  rows: z.array(listRow),
  nextCursor: z.string().nullable(),
});

// ── stats procedures (read aggregators surfaced for the SPA) ──────

/** Signed paise — variance amounts can be negative. */
const signedPaise = z.string().regex(/^-?\d+$/, "signed paise must be an integer string");

export const countsStatsInput = z.object({
  /** The `type=cash` drawer id. */
  accountId: uuid,
  /** Calendar year (e.g. 2026). Defaults to the current UTC year. */
  year: z.number().int().min(2000).max(2100).optional(),
});

export const countsStatsOutput = z.object({
  countsRun: z.number().int().nonnegative(),
  expectedCounts: z.number().int().nonnegative(),
  averageVariancePaise: signedPaise,
  longestCleanStreakMonths: z.number().int().nonnegative(),
  biggestShortPaise: signedPaise,
  biggestOverPaise: signedPaise,
});

export const transferStatsInput = z.object({
  /** Inclusive ISO date. Defaults to the first of the current UTC month. */
  fromDate: isoDate.optional(),
  /** Inclusive ISO date. Defaults to today (UTC). */
  toDate: isoDate.optional(),
});

const transferStatsBucket = z.object({
  paise: moneyPaise,
  count: z.number().int().nonnegative(),
});

export const transferStatsOutput = z.object({
  totalMovedPaise: moneyPaise,
  totalMovedCount: z.number().int().nonnegative(),
  bankToBank: transferStatsBucket,
  bankToCash: transferStatsBucket,
  cashToBank: transferStatsBucket,
  bankToCard: transferStatsBucket,
});

// ── derived TS types ──────────────────────────────────────────────
export type PostCashMovementInput = z.infer<typeof postCashMovementInput>;
export type PostCashMovementOutput = z.infer<typeof postCashMovementOutput>;
export type ReverseCashMovementInput = z.infer<typeof reverseCashMovementInput>;
export type GetCashMovementInput = z.infer<typeof getCashMovementInput>;
export type GetCashMovementOutput = z.infer<typeof getCashMovementOutput>;
export type ListCashMovementsInput = z.infer<typeof listCashMovementsInput>;
export type ListCashMovementsOutput = z.infer<typeof listCashMovementsOutput>;
export type CashMovementListRow = z.infer<typeof listRow>;
export type CashMovementKindWire = z.infer<typeof cashMovementKind>;
export type CountsStatsInput = z.infer<typeof countsStatsInput>;
export type CountsStatsOutput = z.infer<typeof countsStatsOutput>;
export type TransferStatsInput = z.infer<typeof transferStatsInput>;
export type TransferStatsOutput = z.infer<typeof transferStatsOutput>;
